import { html, render } from "lit-html";
import styles from "./styles.css?inline";
import { Store } from "./store";
import { hasGivenConsent, recordConsent, consentText } from "./consent";
import { fetchConsent, streamChat } from "./api";
import { renderSidebar } from "./sidebar";

let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected) return;
  const tag = document.createElement("style");
  tag.setAttribute("data-mxch", "1");
  tag.textContent = styles;
  document.head.appendChild(tag);
  stylesInjected = true;
}

export class ChatWidget {
  private root: HTMLDivElement;
  private store: Store;
  private open = false;
  private busy = false;
  private thinking = false;
  private fontScale = 1;

  constructor(private site: string) {
    injectStyles();
    this.store = new Store(site);
    this.fontScale = parseFloat(localStorage.getItem(`mxch:${site}:font`) ?? "1");
    this.root = document.createElement("div");
    this.root.id = `mxch-${site}`;
    document.body.appendChild(this.root);
    this.applyFontScale();
    this.renderUI();
  }

  private applyFontScale(): void {
    this.root.style.setProperty("--mx-font-scale", String(this.fontScale));
  }

  private fontBigger = (): void => {
    this.fontScale = Math.min(1.4, +(this.fontScale + 0.1).toFixed(2));
    localStorage.setItem(`mxch:${this.site}:font`, String(this.fontScale));
    this.applyFontScale();
    this.renderUI();
  };
  private fontSmaller = (): void => {
    this.fontScale = Math.max(0.8, +(this.fontScale - 0.1).toFixed(2));
    localStorage.setItem(`mxch:${this.site}:font`, String(this.fontScale));
    this.applyFontScale();
    this.renderUI();
  };

  private toggle = (): void => {
    this.open = !this.open;
    this.renderUI();
  };

  private toggleWide = (): void => {
    this.store.wideMode = !this.store.wideMode;
    this.renderUI();
  };

  private togglePand = (): void => {
    this.store.pandOpen = !this.store.pandOpen;
    this.renderUI();
  };

  private collapseSidebar = (): void => {
    this.store.wideMode = false;
    this.renderUI();
  };

  private renderUI(): void {
    const needsConsent = this.open && !hasGivenConsent(this.site);
    const t = consentText();
    const wide = this.open && hasGivenConsent(this.site) && this.store.wideMode;

    render(
      html`
        <button class="mxch-bubble" @click=${this.toggle} aria-label="Open chat">
          ${this.open ? "×" : "💬"}
        </button>
        ${this.open
          ? html`
              <div class="mxch-panel ${wide ? "mxch-wide" : ""}" role="dialog" aria-label="Maximilius chat">
                ${wide ? renderSidebar({
                  lead: this.store.lead,
                  state: this.store.clientState,
                  enrichment: this.store.enrichment,
                  pandOpen: this.store.pandOpen,
                  onTogglePand: this.togglePand,
                  onCollapse: this.collapseSidebar,
                }) : ""}
                <div class="mxch-chat-section">
                  <header>
                    <div class="mxch-header-id">
                      <div class="mxch-avatar" aria-hidden="true">C</div>
                      <div class="mxch-header-text">
                        <div class="mxch-header-name">Claudio</div>
                        <div class="mxch-header-status">Online</div>
                      </div>
                    </div>
                    ${!needsConsent ? html`
                      <div class="mxch-header-actions">
                        <button class="mxch-icon-btn" @click=${this.fontSmaller} title="Tekst kleiner" aria-label="Tekst kleiner">A−</button>
                        <button class="mxch-icon-btn" @click=${this.fontBigger} title="Tekst groter" aria-label="Tekst groter">A+</button>
                        <button class="mxch-icon-btn" @click=${this.toggleWide} title="${wide ? "Smal" : "Breed"}">
                          ${wide ? "⇥" : "⇤"}
                        </button>
                        <button class="mxch-icon-btn" @click=${this.toggle} title="Sluiten">×</button>
                      </div>
                    ` : ""}
                  </header>
                  ${needsConsent
                    ? html`
                        <div class="mxch-consent">
                          <div class="mxch-avatar" aria-hidden="true">C</div>
                          <p>${t.intro}</p>
                          <div class="mxch-consent-actions">
                            <button class="mxch-secondary" @click=${this.toggle}>${t.decline}</button>
                            <button class="mxch-primary" @click=${this.acceptConsent}>${t.accept}</button>
                          </div>
                        </div>
                      `
                    : html`
                        <div class="mxch-messages">
                          ${this.store.messages.map((m) => m.role === "assistant"
                            ? html`<div class="mxch-msg-row mxch-row-assistant">
                                <div class="mxch-msg-avatar">C</div>
                                <div class="mxch-msg mxch-assistant">${m.content}</div>
                              </div>`
                            : html`<div class="mxch-msg-row mxch-row-user">
                                <div class="mxch-msg mxch-user">${m.content}</div>
                              </div>`
                          )}
                          ${this.thinking ? html`
                            <div class="mxch-msg-row mxch-row-assistant" aria-live="polite" aria-label="Claudio denkt na">
                              <div class="mxch-msg-avatar">C</div>
                              <div class="mxch-msg mxch-typing">
                                <span></span><span></span><span></span>
                              </div>
                            </div>
                          ` : ""}
                        </div>
                        <form class="mxch-input" @submit=${this.submit}>
                          <input
                            name="message"
                            type="text"
                            placeholder="Schrijf een bericht..."
                            autocomplete="off"
                            ?disabled=${this.busy}
                          />
                          <button class="mxch-send-btn" type="submit" ?disabled=${this.busy} title="Versturen" aria-label="Versturen">↑</button>
                        </form>
                      `}
                </div>
              </div>
            `
          : ""}
      `,
      this.root,
    );
  }

  private acceptConsent = async (): Promise<void> => {
    try {
      const token = await fetchConsent(this.site, this.store.visitorId);
      this.store.consentToken = token;
      recordConsent(this.site);
      this.renderUI();
    } catch (err) {
      alert("Consent kon niet worden vastgelegd: " + (err as Error).message);
    }
  };

  private submit = async (e: Event): Promise<void> => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const inputEl = form.querySelector('input[name="message"]') as HTMLInputElement;
    const text = inputEl.value.trim();
    if (!text || this.busy) return;
    this.busy = true;
    this.thinking = true;
    inputEl.value = "";
    this.store.appendMessage({ role: "user", content: text });
    this.renderUI();

    try {
      for await (const ev of streamChat({
        site: this.site,
        visitor_id: this.store.visitorId,
        conversation_id: this.store.conversationId,
        message: text,
        consent_token: this.store.consentToken!,
      })) {
        if (ev.type === "conversation") {
          this.store.conversationId = ev.conversation_id;
        } else if (ev.type === "token") {
          if (this.thinking) this.thinking = false;
          this.store.appendAssistantText(ev.text);
          this.renderUI();
        } else if (ev.type === "lead_update") {
          const { type: _t, ...lead } = ev;
          this.store.mergeLead(lead);
          this.renderUI();
        } else if (ev.type === "client_state") {
          const { type: _t, ...state } = ev;
          this.store.setClientState(state);
          this.renderUI();
        } else if (ev.type === "enrichment") {
          const { type: _t, ...enrich } = ev;
          this.store.setEnrichment(enrich);
          this.renderUI();
        } else if (ev.type === "error") {
          this.store.appendAssistantText(`\n\n[fout: ${ev.message}]`);
        }
      }
    } catch (err) {
      this.store.appendAssistantText(`\n\n[verbinding verbroken: ${(err as Error).message}]`);
    } finally {
      this.busy = false;
      this.thinking = false;
      this.renderUI();
    }
  };
}
